import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import { get } from "lib/api";
import { firstMediaUrl } from "lib/media";
import { useAuth } from "context/AuthContext";
import { useCart } from "context/CartContext";
import { currencyForCompany } from "lib/companyProfile";
import { getTextLocale } from "lib/storefront";
import StorefrontLayout from "../components/StorefrontLayout";
import ProductCard from "../components/ProductCard";
import EdsIcon from "../components/EdsIcon";
import { EDS, normalizeTransmissions, resolvePrice, resetButton } from "../theme";

export default function CatalogPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = String(searchParams.get("q") || "");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedTransmission, setSelectedTransmission] = useState("");

  const isMobileRoute =
    location.pathname === "/m/catalog" ||
    location.pathname.startsWith("/m/catalog/");
  const toPath = (path) =>
    isMobileRoute ? `/m${path === "/" ? "" : path}` : path;

  const { addItem } = useCart();
  const { token, activeCompany, storefrontTenant } = useAuth();
  const canOrder = !!token;
  const companyKey = String(storefrontTenant || activeCompany || "eds").toLowerCase();
  const textLocale = getTextLocale();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(q), 300);
    return () => clearTimeout(timer);
  }, [q]);

  const { data, isLoading } = useQuery({
    queryKey: [
      "eds-catalog",
      debouncedSearch,
      companyKey,
      textLocale,
      token ? "auth" : "public",
    ],
    queryFn: () =>
      get("/api/storefront/catalog", {
        q: debouncedSearch || undefined,
        sort: "name:asc",
        pagination: { pageSize: 100 },
      }),
    staleTime: 30000,
  });

  const currency =
    data?.meta?.company?.baseCurrency || currencyForCompany(companyKey);

  const products = useMemo(
    () =>
      (data?.data || []).map((item) => {
        const attrs = item.attributes || item;
        return {
          id: item.id,
          documentId: item.documentId || item.id,
          sku: attrs.sku,
          name: attrs.name,
          images: attrs.images,
          unitPrice: attrs.unitPrice,
          coreCharge: attrs.coreCharge,
          priceLevels: attrs.priceLevels,
          status: attrs.status,
          transmissions: normalizeTransmissions(attrs.transmissions),
        };
      }),
    [data],
  );

  const transmissionOptions = useMemo(() => {
    const set = new Set();
    products.forEach((product) =>
      (product.transmissions || []).forEach((t) => set.add(t)),
    );
    return Array.from(set).sort();
  }, [products]);

  const visibleProducts = useMemo(() => {
    if (!selectedTransmission) return products;
    return products.filter((product) =>
      (product.transmissions || []).some(
        (t) => t.toLowerCase() === selectedTransmission.toLowerCase(),
      ),
    );
  }, [products, selectedTransmission]);

  const openProduct = (product) => {
    navigate(toPath(`/catalog/${product.documentId}`));
  };

  const addProduct = (product) => {
    const price = Number(resolvePrice(product) || 0);
    if (!price) return;
    addItem(
      {
        documentId: product.documentId,
        sku: product.sku,
        name: product.name,
        unitPrice: price,
        coreCharge: product.coreCharge != null ? Number(product.coreCharge) : null,
        imageUrl: firstMediaUrl(product?.images),
      },
      1,
    );
  };

  const setSearch = (value) => {
    const next = String(value || "").trim();
    if (next) setSearchParams({ q: next });
    else setSearchParams({});
  };

  return (
    <StorefrontLayout maxWidth={null}>
      <Container maxWidth="xl" sx={{ pt: { xs: 3, md: 4 }, pb: 1 }}>
        <Box
          component="h1"
          sx={{
            m: 0,
            fontSize: { xs: "1.625rem", md: "2rem" },
            fontWeight: 500,
            letterSpacing: "-0.03em",
            color: EDS.ink,
          }}
        >
          Catalog
        </Box>
        <Box
          component="p"
          sx={{
            mt: 0.5,
            mb: 0,
            color: EDS.muted,
            fontSize: "0.875rem",
            fontWeight: 400,
          }}
        >
          {isLoading
            ? "Loading…"
            : `${visibleProducts.length} product${visibleProducts.length === 1 ? "" : "s"}`}
        </Box>

        <Box
          sx={{
            mt: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            maxWidth: 360,
            borderBottom: `1px solid ${EDS.lineMedium}`,
            pb: 0.6,
            color: EDS.subtle,
          }}
        >
          <EdsIcon name="search" size={18} color={EDS.subtle} />
          <Box
            component="input"
            value={q}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or SKU"
            sx={{
              flex: 1,
              border: 0,
              outline: "none",
              bgcolor: "transparent",
              color: EDS.ink,
              fontSize: "0.9375rem",
              fontWeight: 400,
              fontFamily: "inherit",
              "&::placeholder": { color: EDS.subtle },
            }}
          />
        </Box>
      </Container>

      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
        <Grid container spacing={{ xs: 4, md: 6 }}>
          {transmissionOptions.length ? (
            <Grid item xs={12} md={2.5}>
              <Box sx={{ position: { md: "sticky" }, top: 80 }}>
                <Box
                  component="p"
                  sx={{
                    m: 0,
                    mb: 1,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: EDS.subtle,
                  }}
                >
                  Platform
                </Box>
                {["", ...transmissionOptions].map((transmission) => {
                  const active = selectedTransmission === transmission;
                  const label = transmission || "All";
                  return (
                    <Box
                      key={label}
                      component="button"
                      type="button"
                      onClick={() => setSelectedTransmission(transmission)}
                      sx={{
                        ...resetButton,
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        mb: 0.15,
                        py: 0.5,
                        fontSize: "0.875rem",
                        fontWeight: active ? 500 : 400,
                        color: active ? EDS.ink : EDS.muted,
                        letterSpacing: "-0.01em",
                        "&:hover": { color: EDS.ink },
                      }}
                    >
                      {label}
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          ) : null}

          <Grid item xs={12} md={transmissionOptions.length ? 9.5 : 12}>
            {isLoading ? (
              <Box sx={{ py: 10, textAlign: "center", color: EDS.muted, fontWeight: 400 }}>
                Loading catalog…
              </Box>
            ) : visibleProducts.length === 0 ? (
              <Box sx={{ py: 10, textAlign: "center", color: EDS.muted, fontWeight: 400 }}>
                No products match your search.
              </Box>
            ) : (
              <Grid container spacing={{ xs: 3, md: 4 }}>
                {visibleProducts.map((product) => (
                  <Grid item xs={6} sm={4} lg={3} key={product.documentId}>
                    <ProductCard
                      product={product}
                      currency={currency}
                      onOpen={() => openProduct(product)}
                      onAdd={addProduct}
                      canOrder={canOrder}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Container>
    </StorefrontLayout>
  );
}
